import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Radio,
    RadioGroup,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { screensApi } from './Screens.api';
import { screenDialogBodySx } from './dialogSx';
import { ScreenClientRelease, formatBytes } from './Screens.types';

export interface UpgradeDialogProps {
    open: boolean;
    /** Screens the version is deployed to (names for the title). */
    screenNames: string[];
    currentVersion?: string;
    onClose: () => void;
    onConfirm: (version: string) => Promise<void>;
}

/**
 * Picks the st-ck-screen-client version to deploy. The choice becomes the screen's target version:
 * on its next poll the device downloads that package from Nexus, installs it and reboots.
 */
export const UpgradeDialog: React.FC<UpgradeDialogProps> = ({ open, screenNames, currentVersion, onClose, onConfirm }) => {
    const [releases, setReleases] = useState<ScreenClientRelease[]>([]);
    const [selected, setSelected] = useState('');
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = (refresh = false) => {
        setLoading(true);
        setError(null);
        screensApi
            .releases(refresh)
            .then((list) => {
                setReleases(list);
                setSelected((s) => s || list.find((r) => r.version !== currentVersion)?.version || '');
            })
            .catch((err) => setError(err instanceof Error ? err.message : 'Could not load versions'))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (open) {
            setSelected('');
            load();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const confirm = async () => {
        setBusy(true);
        setError(null);
        try {
            await onConfirm(selected);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Could not schedule the upgrade');
        } finally {
            setBusy(false);
        }
    };

    const title = screenNames.length === 1 ? screenNames[0] : `${screenNames.length} screens`;

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
                Deploy screen client version · {title}
                <Box sx={{ flexGrow: 1 }} />
                <Tooltip title="Reload versions from Nexus">
                    <IconButton size="small" onClick={() => load(true)} disabled={loading}>
                        <RefreshIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </DialogTitle>
            <DialogContent dividers sx={screenDialogBodySx}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    The screen downloads the selected package from Nexus on its next poll, installs it and reboots so the new version takes
                    effect.{currentVersion ? ` Currently running ${currentVersion}.` : ''}
                </Typography>
                {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress color="secondary" />
                    </Box>
                ) : releases.length === 0 ? (
                    <Alert severity="info">No versions published in Nexus yet.</Alert>
                ) : (
                    <RadioGroup value={selected} onChange={(e) => setSelected(e.target.value)}>
                        {releases.map((r) => (
                            <FormControlLabel
                                key={r.version}
                                value={r.version}
                                control={<Radio />}
                                sx={{ alignItems: 'flex-start', mb: 1 }}
                                label={
                                    <Box sx={{ pt: 0.75 }}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Typography sx={{ fontWeight: 600, fontFamily: 'monospace' }}>{r.version}</Typography>
                                            <Chip size="small" label={r.channel === 'dev' ? 'dev build' : 'release'} color={r.channel === 'dev' ? 'default' : 'success'} />
                                            {r.version === currentVersion && <Chip size="small" variant="outlined" label="installed" />}
                                        </Stack>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                            {r.builtAt ? new Date(r.builtAt).toLocaleString() : ''}
                                            {r.gitCommit ? ` · ${r.gitBranch || 'main'}@${r.gitCommit.slice(0, 7)}` : ''}
                                            {r.artifacts.length ? ` · ${r.artifacts.map((a) => `${a.arch} ${formatBytes(a.sizeBytes)}`).join(', ')}` : ''}
                                        </Typography>
                                        {r.notes && (
                                            <Typography variant="caption" sx={{ display: 'block', whiteSpace: 'pre-wrap' }}>
                                                {r.notes}
                                            </Typography>
                                        )}
                                    </Box>
                                }
                            />
                        ))}
                    </RadioGroup>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={busy}>
                    Cancel
                </Button>
                <Button variant="contained" color="secondary" disabled={!selected || busy} onClick={() => void confirm()}>
                    Deploy {selected}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UpgradeDialog;
